import { z } from 'zod';

/**
 * Jarvis MCP client contracts (B6 / IDEA-81 — ADR 0091).
 *
 * B6 lets Jarvis call tools from **pinned external MCP servers**. The repo already has the
 * symmetric half — a private MCP *server* (`services/finance-api/src/mcp.ts`, plain JSON-RPC 2.0
 * over POST, no SDK — ADR 0076); these are the shapes the hand-rolled MCP *client* speaks and
 * parses. Everything a server sends (`initialize` / `tools/list` / `tools/call` responses) is
 * EXTERNAL, untrusted input, so it is parsed defensively and size-capped here (parse, don't
 * validate — ADR 0005). Two security invariants live in these shapes (ADR 0091 §2/§3):
 *
 * 1. A server's tool `description` is NEVER forwarded to the model — the offer layer substitutes
 *    OUR fixed local description (from the operator's allowlist). The server's is parsed only so
 *    it can be dropped.
 * 2. A `tools/call` result is untrusted content — fenced-as-data and size-capped.
 *
 * No new dependency: this mirrors `mcp.ts`'s JSON-RPC framing exactly.
 */

/** The MCP protocol version the client advertises (mirrors `finance-api/src/mcp.ts`). */
export const MCP_PROTOCOL_VERSION = '2025-06-18' as const;

// ── Size caps (bounds on EXTERNAL input + operator config) ──────────────────────

/** Longest pinned-server id (a lowercase namespace segment in `mcp.<id>.<tool>`). */
export const MCP_SERVER_ID_MAX_CHARS = 40;
/** Longest tool name (server-side raw name; also the tail of the offered `mcp.<id>.<tool>`). */
export const MCP_TOOL_NAME_MAX_CHARS = 64;
/** Longest OUR-fixed tool description an operator may pin (kept tight — it rides every prompt). */
export const MCP_TOOL_DESCRIPTION_MAX_CHARS = 400;
/** Max tools an operator may allowlist per pinned server. */
export const MCP_TOOLS_PER_SERVER_MAX = 16;
/** Max pinned servers. */
export const MCP_SERVERS_MAX = 8;
/** Max tools a server's `tools/list` may declare (bounds a malicious server's fan-out). */
export const MCP_TOOLS_LIST_MAX = 128;
/**
 * Max content blocks a `tools/call` result may carry. Bounds a malicious server's fan-out at the
 * PARSE boundary: without it a server could send millions of ≤{@link MCP_CONTENT_BLOCK_HARD_MAX_CHARS}
 * blocks and OOM the isolate before {@link MCP_TOOL_RESULT_MAX_CHARS} (applied to the JOINED text
 * only AFTER the whole array materialises) ever runs. 64 × 64KiB ≈ 4MiB worst case, then truncated.
 */
export const MCP_TOOL_RESULT_BLOCKS_MAX = 64;
/**
 * Cap applied to a normalised `tools/call` result at resolution time — the fenced, model-facing
 * text. Matches `JARVIS_TOOL_RESULT_MAX_CHARS` so the two caps agree; `boundedResultJson` is the
 * backstop on the prompt carrier.
 */
export const MCP_TOOL_RESULT_MAX_CHARS = 8_000;
/**
 * Hard OOM ceiling on a single `tools/call` content block's text at the PARSE boundary — well
 * above {@link MCP_TOOL_RESULT_MAX_CHARS} (the client truncates the joined text to that). A block
 * beyond this is an absurd response and fails parsing (fail-closed), never allocated further.
 */
export const MCP_CONTENT_BLOCK_HARD_MAX_CHARS = 64 * 1024;
/** Ceiling on a server's self-description length at the parse boundary (it is dropped anyway). */
export const MCP_SERVER_DESCRIPTION_HARD_MAX_CHARS = 8_000;
/** Ceiling on a JSON-RPC error message length. */
export const MCP_RPC_ERROR_MESSAGE_MAX_CHARS = 500;

/** Pinned-server ids are a lowercase namespace segment (no dots — they delimit `mcp.<id>.<tool>`). */
const MCP_SERVER_ID_RE = /^[a-z0-9][a-z0-9-]*$/;
/** Tool names allow the common MCP charset (letters/digits/`_`/`.`/`-`). */
const MCP_TOOL_NAME_RE = /^[A-Za-z0-9_.-]+$/;

// ── Operator config: a pinned server (parsed from the MCP_SERVERS env JSON) ──────
//
// STRICT (operator-authored) so a typo fails loud at compose (the parseOwnerConfig posture).
// Tokens are NOT in this JSON — `tokenEnvRef` names a SEPARATE env var; the resolved bearer stays
// server-side only (never on the wire, never logged, never returned).

/** One allowlisted tool: its raw server-side `name` + OUR fixed local `description`. */
export const McpAllowlistedToolSchema = z
  .object({
    name: z.string().min(1).max(MCP_TOOL_NAME_MAX_CHARS).regex(MCP_TOOL_NAME_RE),
    /** Fixed LOCAL description forwarded to the model — the server's is dropped (ADR 0091 §2). */
    description: z.string().min(1).max(MCP_TOOL_DESCRIPTION_MAX_CHARS),
  })
  .strict();
export type McpAllowlistedTool = z.infer<typeof McpAllowlistedToolSchema>;

/** A pinned MCP server the operator configured (URL + token-env-ref + allowlisted tools). */
export const McpServerConfigSchema = z
  .object({
    id: z.string().min(1).max(MCP_SERVER_ID_MAX_CHARS).regex(MCP_SERVER_ID_RE),
    url: z.string().url(),
    /** Name of a SEPARATE env var holding this server's outbound bearer token (optional). */
    tokenEnvRef: z.string().min(1).max(120).optional(),
    tools: z.array(McpAllowlistedToolSchema).min(1).max(MCP_TOOLS_PER_SERVER_MAX),
    /**
     * Deliberately pin an internal MCP host over a private tunnel — relaxes the SSRF private-IP
     * block for THIS server only (still http(s)-only, still no URL creds). Default false: the SSRF
     * guard blocks private/loopback/link-local by default (ADR 0091 §5).
     */
    allowPrivate: z.boolean().optional(),
  })
  .strict();
export type McpServerConfig = z.infer<typeof McpServerConfigSchema>;

/** The whole `MCP_SERVERS` JSON payload — an array of pinned servers, bounded. */
export const McpServersConfigSchema = z.array(McpServerConfigSchema).max(MCP_SERVERS_MAX);
export type McpServersConfig = z.infer<typeof McpServersConfigSchema>;

// ── Normalised tool shapes (what the client returns / the offer + resolver consume) ──

/**
 * A tool as the client parsed it from a server's `tools/list` — name + the server's (untrusted)
 * description + its input schema. The offer layer keeps `name` (allowlist-filtered), DROPS
 * `description` (substituting our fixed one), and bounds `inputSchema`. `description` is present
 * only so it can be provably dropped.
 */
export const McpToolDeclSchema = z.object({
  name: z.string().min(1).max(MCP_TOOL_NAME_MAX_CHARS),
  description: z.string().max(MCP_SERVER_DESCRIPTION_HARD_MAX_CHARS).optional(),
  inputSchema: z.record(z.unknown()).optional(),
});
export type McpToolDecl = z.infer<typeof McpToolDeclSchema>;

/** A normalised `tools/call` result — the fenced text + the tool-level error flag. */
export const McpToolResultSchema = z.object({
  /** Joined, truncated text content (fenced-as-data; ≤ {@link MCP_TOOL_RESULT_MAX_CHARS}). */
  content: z.string(),
  /** MCP convention: a tool-level failure rides IN the result, not as a protocol error. */
  isError: z.boolean(),
});
export type McpToolResult = z.infer<typeof McpToolResultSchema>;

// ── JSON-RPC 2.0 envelopes (mirror of mcp.ts; the server side is EXTERNAL, parsed lenient) ──

export const JsonRpcIdSchema = z.union([z.string(), z.number(), z.null()]);
export type JsonRpcId = z.infer<typeof JsonRpcIdSchema>;

/** A JSON-RPC request the client SENDS (built here so both wire sides agree on the framing). */
export const JsonRpcRequestSchema = z.object({
  jsonrpc: z.literal('2.0'),
  id: JsonRpcIdSchema,
  method: z.string().min(1),
  params: z.record(z.unknown()).optional(),
});
export type JsonRpcRequest = z.infer<typeof JsonRpcRequestSchema>;

/** The `error` member of a JSON-RPC failure response. */
export const JsonRpcErrorSchema = z.object({
  code: z.number(),
  message: z.string().max(MCP_RPC_ERROR_MESSAGE_MAX_CHARS),
  data: z.unknown().optional(),
});
export type JsonRpcError = z.infer<typeof JsonRpcErrorSchema>;

/**
 * A JSON-RPC response the client RECEIVES — EXTERNAL input, parsed defensively (not strict; a
 * server may add fields). Exactly one of `result` / `error` is meaningful; the client checks
 * `error` first, then reads the method-specific `result` shape.
 */
export const JsonRpcResponseSchema = z.object({
  jsonrpc: z.literal('2.0'),
  id: JsonRpcIdSchema.optional(),
  result: z.unknown().optional(),
  error: JsonRpcErrorSchema.optional(),
});
export type JsonRpcResponse = z.infer<typeof JsonRpcResponseSchema>;

/** `initialize` result (only the fields the client reads; lenient on the rest). */
export const McpInitializeResultSchema = z.object({
  protocolVersion: z.string().optional(),
  serverInfo: z.object({ name: z.string().optional() }).partial().passthrough().optional(),
  capabilities: z.record(z.unknown()).optional(),
});
export type McpInitializeResult = z.infer<typeof McpInitializeResultSchema>;

/** One tool in a server's `tools/list` — the server's raw declaration (untrusted). */
export const McpServerToolSchema = z.object({
  name: z.string().min(1).max(MCP_TOOL_NAME_MAX_CHARS),
  /** The server's self-description — parsed only to be DROPPED (ADR 0091 §2). */
  description: z.string().max(MCP_SERVER_DESCRIPTION_HARD_MAX_CHARS).optional(),
  inputSchema: z.record(z.unknown()).optional(),
});
export type McpServerTool = z.infer<typeof McpServerToolSchema>;

/** `tools/list` result — the tool array, bounded. */
export const McpToolsListResultSchema = z.object({
  tools: z.array(McpServerToolSchema).max(MCP_TOOLS_LIST_MAX),
});
export type McpToolsListResult = z.infer<typeof McpToolsListResultSchema>;

/** One `tools/call` content block (text is what the client keeps; other block types ignored). */
export const McpContentBlockSchema = z
  .object({
    type: z.string(),
    text: z.string().max(MCP_CONTENT_BLOCK_HARD_MAX_CHARS).optional(),
  })
  .passthrough();
export type McpContentBlock = z.infer<typeof McpContentBlockSchema>;

/** `tools/call` result — content blocks + the tool-level error flag (MCP convention). */
export const McpToolsCallResultSchema = z.object({
  content: z.array(McpContentBlockSchema).max(MCP_TOOL_RESULT_BLOCKS_MAX).optional(),
  isError: z.boolean().optional(),
});
export type McpToolsCallResult = z.infer<typeof McpToolsCallResultSchema>;

// ── Offered-tool naming (the `mcp.<server>.<tool>` namespace contract) ───────────

/** Build the model-facing tool name for a pinned server's tool. */
export function mcpOfferedToolName(serverId: string, toolName: string): string {
  return `mcp.${serverId}.${toolName}`;
}

/** Does this look like an offered MCP tool name? (Cheap prefix check for the mapper.) */
export function isMcpOfferedToolName(name: string): boolean {
  return name.startsWith('mcp.');
}
